'use client';
import { getCookie, setCookie } from 'cookies-next/client';
import { Shield, X } from 'lucide-react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';

const COOKIE_CONSENT_KEY = 'cookie-accept';
const COOKIE_EXPIRY_DAYS = 365;

const CookieConsent = () => {
  const tComponents = useTranslations('components');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Skip cookie consent in E2E tests
    if (process.env.NEXT_PUBLIC_DISABLE_COOKIE_CONSENT === 'true') {
      return;
    }

    // Skip in standalone PWA mode — separate storage from browser,
    // and users who installed the app have already engaged
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone);
    if (isStandalone) {
      return;
    }

    const consent = getCookie(COOKIE_CONSENT_KEY);
    if (!consent) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + COOKIE_EXPIRY_DAYS);

    setCookie(COOKIE_CONSENT_KEY, 'accepted', {
      expires: expiryDate,
      sameSite: 'strict',
      secure: process.env.NODE_ENV === 'production',
      path: '/',
    });
    setIsVisible(false);
  };

  const handleDecline = () => {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + COOKIE_EXPIRY_DAYS);

    setCookie(COOKIE_CONSENT_KEY, 'declined', {
      expires: expiryDate,
      sameSite: 'strict',
      secure: process.env.NODE_ENV === 'production',
      path: '/',
    });
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-card border-t shadow-lg z-50 transform transition-transform duration-500 ease-in-out">
      <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 align-start">
            <Shield className="h-5 w-5 text-blue-600 min-w-5" />
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                {tComponents('cookies.description')} Read our{' '}
                <Link
                  href={`/legal/privacy`}
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  {tComponents('cookies.privacyPolicy')}
                </Link>{' '}
                and{' '}
                <Link href={`/legal/terms`} className="text-blue-600 hover:text-blue-700 underline">
                  {tComponents('cookies.termsOfService')}
                </Link>{' '}
                {tComponents('cookies.moreInfo')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDecline}
              className="text-muted-foreground hover:text-foreground"
            >
              {tComponents('cookies.decline')}
            </Button>
            <Button
              size="sm"
              onClick={handleAccept}
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              {tComponents('cookies.accept')}
            </Button>
            <button
              onClick={handleDecline}
              className="p-1 hover:bg-muted rounded-full transition-colors"
              aria-label={tComponents('cookies.close')}
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
